import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { openModal } from '../../stores/modal.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { LifecycleStepper, NextActionCard, type LifecycleAction } from '../../components/ui/LifecycleStepper';
import { MoneyThread } from '../../components/ui/MoneyThread';
import { ActionsMenu, type ActionItem } from '../../components/ui/ActionsMenu';
import { Chip } from '../../components/ui/Chip';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { Helper } from '../../components/ui/Helper';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur, fmtDate } from '../../lib/utils';
import { adminLinks } from '../../lib/adminRoutes';

interface QuoteFull {
  id: string;
  number: string | null;
  title: string | null;
  introMessage: string | null;
  termsAndConditions: string | null;
  notes: string | null;
  status: string;
  issueDate: string | null;
  validUntil: string | null;
  subtotal: string; vat: string; total: string;
  sentAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  signerName: string | null;
  signerEmail: string | null;
  signerIp: string | null;
  signatureDataUrl: string | null;
  documentHash: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  convertedToInvoiceId: string | null;
  convertedAt: string | null;
  convertedToInvoice: { id: string; number: string | null; status: string } | null;
  signatureToken: string | null;
  client: { id: string; legalName: string; vat: string | null; sdiCode: string | null; pec: string | null };
  project: { id: string; code: string; name: string } | null;
  lines: Array<{ id: string; description: string; quantity: string; unitPrice: string; vatRate: string; order: number }>;
}

interface ClientContact { id: string; fullName: string; email: string | null; isPrimary: boolean; }

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sendModalOpen, setSendModalOpen] = useState(false);

  const { data: q, isLoading, isError } = useQuery({
    queryKey: ['quote', id],
    queryFn: () => api.get<QuoteFull>(`/quotes/${id}`),
    enabled: !!id,
    retry: false,
  });

  const sendQuote = useMutation({
    mutationFn: (payload: { to: string; cc?: string; subject: string; message: string }) =>
      api.post(`/quotes/${id}/send`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quote', id] });
      qc.invalidateQueries({ queryKey: ['quotes'] });
      setSendModalOpen(false);
      toast.ok('Preventivo inviato', 'Il cliente riceverà un\'email con il link per firmare.');
    },
    onError: (e: any) => toast.alert('Invio fallito', e?.message ?? 'Riprova'),
  });

  const convert = useMutation({
    mutationFn: () => api.post<{ invoiceId: string }>(`/quotes/${id}/convert-to-invoice`),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['quote', id] });
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.ok('Convertito in fattura', 'Apro la nuova fattura…');
      navigate(`/invoices/${res.invoiceId}`);
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  const duplicate = useMutation({
    mutationFn: () => api.post<{ id: string }>(`/quotes/${id}/duplicate`),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      toast.ok('Duplicato', 'Apro la copia in bozza…');
      navigate(`/quotes/${res.id}`);
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  // Rifiuto reversibile: niente ConfirmDialog, si annulla dal toast (pattern Gmail).
  // `prevStatus` viaggia nelle variables della mutation per ripristinare lo stato.
  const reject = useMutation({
    mutationFn: (vars: { reason: string; prevStatus: string }) =>
      api.post(`/quotes/${id}/reject`, { reason: vars.reason }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['quote', id] });
      qc.invalidateQueries({ queryKey: ['quotes'] });
      toast.undo('Preventivo rifiutato', {
        onUndo: () =>
          api.patch(`/quotes/${id}`, { status: vars.prevStatus }).then(() => {
            qc.invalidateQueries({ queryKey: ['quote', id] });
            qc.invalidateQueries({ queryKey: ['quotes'] });
          }),
      });
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/quotes/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      toast.ok('Preventivo eliminato');
      navigate(adminLinks.preventivi());
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  if (!id) return <Navigate to={adminLinks.preventivi()} replace />;
  if (isError) return (
    <div className="page quote-detail-page">
      <Breadcrumb items={[{ label: 'Preventivi', to: adminLinks.preventivi() }, { label: q?.number ?? 'Preventivo' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Preventivo non trovato</h1>
      </div>
    </div>
  );
  if (isLoading || !q) return <DetailSkeleton header="chips" />;

  const isDraft = q.status === 'BOZZA';
  const isSent = q.status === 'INVIATO' || q.status === 'VISUALIZZATO';
  const isSigned = q.status === 'ACCETTATO';
  const isConverted = q.status === 'CONVERTITO';
  const isRejected = q.status === 'RIFIUTATO' || q.status === 'SCADUTO';

  const stageIdx = isConverted ? 4 : isSigned ? 3 : q.status === 'VISUALIZZATO' ? 2 : isSent ? 1 : 0;
  const signLink = q.signatureToken ? `${window.location.origin}/sign/quotes/${q.signatureToken}` : null;

  // ───── Filo dei soldi: Preventivo → Progetto → Fattura → Incasso ─────
  // Contratto MoneyThread: { key, label, sub?, to?, state: done|current|future }.
  const moneySteps: Array<{ key: string; label: string; sub?: string; to?: string; state: 'done' | 'current' | 'future' }> = [
    { key: 'preventivo', label: 'Preventivo', sub: `€ ${fmtEur(q.total)}`, state: isSigned || isConverted ? 'done' : 'current' },
    q.project
      ? { key: 'progetto', label: 'Progetto', sub: q.project.code, to: `/projects/${q.project.id}`, state: 'done' }
      : isSigned
        ? { key: 'progetto', label: 'Progetto', sub: 'Da creare', state: 'future' }
        : { key: 'progetto', label: 'Progetto', state: 'future' },
    q.convertedToInvoice
      ? { key: 'fattura', label: 'Fattura', sub: q.convertedToInvoice.number ?? undefined, to: `/invoices/${q.convertedToInvoice.id}`, state: 'done' }
      : { key: 'fattura', label: 'Fattura', state: 'future' },
    { key: 'incasso', label: 'Incasso', state: q.convertedToInvoice?.status === 'PAGATA' ? 'done' : 'future' },
  ];

  // CTA "Crea progetto da preventivo": firmato, nessun progetto collegato e non
  // ancora fatturato → è il passo che apre il filo dei soldi. Il payload è il
  // contratto col flow NewProject: { clientId, quoteId, title, value }.
  const showCreateProject = isSigned && !q.project && !q.convertedToInvoice;

  // `key` mappa la next-action sulla voce omologa del menu Azioni (excludeKey).
  const nextAction: (LifecycleAction & { key: string }) | null = (() => {
    if (isDraft) return {
      key: 'send',
      title: 'Pronto per essere inviato',
      body: 'Manda il preventivo al cliente: riceverà un\'email con un link sicuro per firmarlo digitalmente — è gratuito e legalmente valido (eIDAS).',
      cta: 'Invia al cliente',
      icon: 'send' as IconName,
      tone: 'primary' as const,
      onClick: () => setSendModalOpen(true),
    };
    if (isSent) return {
      key: 'resend',
      title: 'In attesa di firma',
      body: 'Il cliente ha ricevuto il link. Se ci mette troppo, puoi rinviarglielo o sollecitare via altri canali.',
      cta: 'Reinvia link',
      icon: 'mail' as IconName,
      tone: 'info' as const,
      onClick: () => setSendModalOpen(true),
    };
    if (isSigned) return {
      key: 'convert',
      title: 'Firmato — pronto per essere fatturato',
      body: 'Il cliente ha accettato e firmato. Con un click crei la bozza di fattura con righe e cliente già compilati.',
      cta: 'Genera fattura',
      icon: 'invoice' as IconName,
      tone: 'ok' as const,
      onClick: () => convert.mutate(),
      loading: convert.isPending,
    };
    if (isConverted && q.convertedToInvoice) return {
      key: 'open-invoice',
      title: 'Già convertito in fattura',
      body: `Questo preventivo è stato convertito nella fattura ${q.convertedToInvoice.number ?? '—'}.`,
      cta: 'Apri fattura',
      icon: 'arrowRight' as IconName,
      tone: 'info' as const,
      onClick: () => navigate(`/invoices/${q.convertedToInvoice!.id}`),
    };
    return null;
  })();

  // Menu "Azioni" (ActionsMenu condiviso): stesse voci del vecchio dropdown
  // locale, MENO "Copia link firma" — casa unica: card "Link firma cliente".
  const actionItems: ActionItem[] = [
    { key: 'resend',    label: 'Reinvia link al cliente', icon: 'mail',    onClick: () => setSendModalOpen(true), hidden: !isSent },
    { key: 'convert',   label: 'Genera fattura',          icon: 'invoice', onClick: () => convert.mutate(), hidden: !isSigned },
    { key: 'duplicate', label: 'Duplica come bozza',      icon: 'file',    onClick: () => duplicate.mutate() },
    {
      key: 'reject', label: 'Marca come rifiutato', icon: 'x',
      hidden: isSigned || isConverted || isRejected,
      // Azione reversibile → niente conferma: il toast "Annulla" rimette lo stato precedente.
      onClick: () => reject.mutate({ reason: 'Rifiutato internamente', prevStatus: q.status }),
    },
    { key: 'print', label: 'Stampa PDF', icon: 'export', onClick: () => { window.print(); } },
    {
      key: 'delete', label: 'Elimina', icon: 'trash', danger: true,
      hidden: isConverted,
      onClick: async () => {
        if (await confirmDialog({
          title: 'Eliminare definitivamente questo preventivo?',
          danger: true,
          confirmLabel: 'Elimina',
        })) del.mutate();
      },
    },
  ];

  // Blocco "Link firma cliente": casa unica del link firma, NUDO sul foglio —
  // kicker + azione "copia" a destra. Quando il preventivo è INVIATO/VISUALIZZATO
  // va in CIMA alla colonna destra.
  const signLinkCard = signLink && !isDraft ? (
    <div>
      <div className="data-hd">
        <span className="t-micro">Link firma cliente</span>
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => {
            navigator.clipboard.writeText(signLink);
            toast.ok('Link copiato', 'Incollalo dove vuoi (WhatsApp, Slack, ecc.)');
          }}
        >
          <Icon name="link" size={10} /> Copia link
        </button>
      </div>
      <div className="mono text-[12px] mute break-all p-2 bg-paper-2 rounded-1">{signLink}</div>
    </div>
  ) : null;

  return (
    <div className="page quote-detail-page">
      <Breadcrumb items={[{ label: 'Preventivi', to: adminLinks.preventivi() }, { label: q?.number ?? 'Preventivo' }]} />

      {/* RIFACIMENTO EDITORIALE: solo l'header — titolo grande e leggero, hairline aurora */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px]">
          {/* L1 protagonista: il titolo della proposta, tronca se lungo */}
          <h1 className="ed-title truncate">{q.title || `Proposta per ${q.client.legalName}`}</h1>
          {/* L3 meta-indice: preventivo · numero · validità, una riga mute */}
          <div className="detail-meta mt-2 truncate">
            <span>preventivo</span>
            {q.number && <><span className="mute-2">·</span><span className="mono">{q.number}</span></>}
            {q.validUntil && <><span className="mute-2">·</span><span><span className="mute-2 mr-1.5">Valido fino</span><span className="v">{fmtDate(q.validUntil)}</span></span></>}
          </div>
          {/* L4 più arretrata: cliente + progetto, label mute, valori --text peso 400 */}
          <div className="detail-meta mt-1 truncate">
            <span><span className="mute-2 mr-1.5">Cliente</span><span className="v">{q.client.legalName}</span></span>
            {q.project && (
              <><span className="mute-2">·</span>
              <span><span className="mute-2 mr-1.5">Progetto</span><a className="v mono hover:underline cursor-pointer" onClick={() => navigate(`/projects/${q.project!.id}`)}>{q.project.code}</a></span></>
            )}
          </div>
          {/* Stato che "urla" (convertito → fattura): riga dedicata SOTTO, mai in cima */}
          {q.convertedToInvoice && (
            <div className="flex gap-1.5 mt-2.5 items-center">
              <Chip tone="ok" className="cursor-pointer" onClick={() => navigate(`/invoices/${q.convertedToInvoice!.id}`)}>
                <Icon name="invoice" size={10} /> Fattura {q.convertedToInvoice.number ?? '—'}
              </Chip>
            </div>
          )}
        </div>

        <div className="flex gap-2 pb-1 d-only">
          <ActionsMenu
            variant="ghost"
            label="Azioni"
            items={actionItems}
            excludeKey={nextAction?.key}
            disabled={sendQuote.isPending || convert.isPending || duplicate.isPending || reject.isPending || del.isPending}
          />
        </div>
      </div>

      {/* Card prominente: firmato senza progetto → crea il progetto dal preventivo */}
      {showCreateProject && (
        <DesktopOnly
          cosa="La creazione del progetto dal preventivo firmato"
          perche="apre una procedura a più passaggi con responsabilità e scadenze da definire."
        >
          <div className="mb-5">
            <NextActionCard
              action={{
                title: 'Crea il progetto da questo preventivo',
                body: 'Il cliente ha firmato: apri il progetto operativo con cliente, titolo e valore già precompilati dalla proposta.',
                cta: 'Crea progetto',
                icon: 'cube',
                tone: 'primary',
                onClick: () => openModal('newProject', {
                  clientId: q.client.id,
                  quoteId: id,
                  title: q.title || `Proposta per ${q.client.legalName}`,
                  value: Number(q.total) || 0,
                }),
              }}
            />
          </div>
        </DesktopOnly>
      )}

      {isRejected ? (
        <div className="card mb-6">
          <div className="card-bd py-4 flex items-center gap-3 text-alert">
            <Icon name="x" size={18} />
            <strong>Preventivo {q.status === 'SCADUTO' ? 'scaduto' : 'rifiutato'}</strong>
            {q.rejectionReason && <span className="mute text-[13px]">— {q.rejectionReason}</span>}
          </div>
        </div>
      ) : (
        <>
        {/* Filo dei soldi sopra il ciclo di vita: dove sono i soldi, ora */}
        <div className="mb-5">
          <MoneyThread steps={moneySteps} />
        </div>
        <LifecycleStepper
          steps={[
            { key: 'bozza', label: 'Bozza', icon: 'file', sub: 'In lavorazione' },
            { key: 'inviato', label: 'Inviato', icon: 'send', sub: 'Link inviato' },
            { key: 'visto', label: 'Visto', icon: 'eye', sub: 'Cliente ha aperto' },
            { key: 'firmato', label: 'Firmato', icon: 'check', sub: 'Accettato' },
            { key: 'fatturato', label: 'Fatturato', icon: 'invoice', sub: 'Convertito' },
          ]}
          currentIdx={stageIdx}
        />
        </>
      )}

      {nextAction && !isRejected && (
        <div className="mb-6">
          <NextActionCard action={nextAction} />
        </div>
      )}

      <div className="m-desk-note m-only mb-5" role="note">
        <Icon name="help" size={14} />
        <span><strong>Duplicazione, stampa ed eliminazione del preventivo</strong> si fanno dal computer; invio, reinvio e apertura della fattura restano qui quando servono.</span>
      </div>

      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div className="min-w-0">
          {q.introMessage && (
            /* Blocco nudo: kicker + prosa, niente gabbia */
            <>
              <div>
                <div className="data-hd">
                  <span className="t-micro">Messaggio al cliente</span>
                </div>
                <div className="whitespace-pre-wrap leading-[1.55]">{q.introMessage}</div>
              </div>
              <div className="ed-divider" />
            </>
          )}

          {/* Voci della proposta: tabella nuda sul foglio — kicker + conteggio, niente gabbia */}
          <div className="ed-section">
            <div className="data-hd">
              <span className="t-micro">Voci della proposta <span className="mono">· {q.lines.length}</span></span>
            </div>
            <table className="tbl tbl--ed tbl--card tbl--static">
              <thead><tr><th>Descrizione</th><th className="num">Q.tà</th><th className="num">€ unit</th><th className="num">IVA</th><th className="num">Totale</th></tr></thead>
              <tbody>
                {q.lines.map((l) => {
                  const qty = Number(l.quantity) || 0;
                  const price = Number(l.unitPrice) || 0;
                  const vatRate = Number(l.vatRate) || 0;
                  const lineTotal = qty * price * (1 + vatRate / 100);
                  return (
                    <tr key={l.id}>
                      <td data-m="hd">{l.description}</td>
                      <td data-m="key" data-l="Quantità" className="num mono">{qty}</td>
                      <td data-m="key" data-l="Prezzo unitario" className="num mono">€ {fmtEur(price)}</td>
                      <td data-m="key" data-l="IVA" className="num mono mute">{vatRate}%</td>
                      <td data-m="key" data-l="Totale" className="num mono font-semibold">€ {fmtEur(lineTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Totali in calce a destra, come su carta: hairline, niente gabbia.
                pr-5 allinea i numeri al padding (20px) dell'ultima colonna. */}
            <div className="flex justify-end pr-5 mt-1">
              <div className="w-[300px] max-w-full text-[13px]">
                <div className="flex justify-between items-baseline py-2 border-b border-line">
                  <span className="mute">Imponibile</span>
                  <span className="mono">€ {fmtEur(q.subtotal)}</span>
                </div>
                <div className="flex justify-between items-baseline py-2 border-b border-line">
                  <span className="mute">IVA</span>
                  <span className="mono">€ {fmtEur(q.vat)}</span>
                </div>
                <div className="flex justify-between items-baseline pt-3">
                  <span className="t-micro">Totale</span>
                  {/* Il totale è LA metrica della pagina: viola, mai semaforo */}
                  <span className="mono tbl-metric accent text-[20px]">€ {fmtEur(q.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {q.termsAndConditions && (
            <>
              <div className="ed-divider" />
              <div>
                <div className="data-hd">
                  <span className="t-micro">Termini e condizioni</span>
                </div>
                <div className="text-[13px] whitespace-pre-wrap font-mono leading-[1.55] mute">{q.termsAndConditions}</div>
              </div>
            </>
          )}

          {q.notes && (
            <>
              <div className="ed-divider" />
              <div>
                <div className="data-hd">
                  <span className="t-micro">Note interne <Helper text="Solo team interno: queste note non sono visibili al cliente." /></span>
                </div>
                <div className="text-[13px] whitespace-pre-wrap">{q.notes}</div>
              </div>
            </>
          )}
        </div>

        <div className="min-w-0">
          {isSent && signLinkCard && (
            <>
              {signLinkCard}
              <div className="ed-divider" />
            </>
          )}

          {/* Anagrafica nuda: kicker + chiave-valore hairline, azione nel kicker */}
          <div>
            <div className="data-hd">
              <span className="t-micro">Cliente</span>
              <button className="btn btn-sm btn-ghost" onClick={() => navigate(`/clients/${q.client.id}`)}>
                Apri scheda <Icon name="arrowRight" size={10} />
              </button>
            </div>
            <div className="ed-kv">
              <InfoLine label="Ragione sociale" value={q.client.legalName} />
              {q.client.vat && <InfoLine label="P.IVA" value={q.client.vat} mono />}
            </div>
          </div>

          {(q.sentAt || q.viewedAt || q.signedAt) && (
            <>
              <div className="ed-divider" />
              <div>
                <div className="data-hd">
                  <span className="t-micro">Tracciamento</span>
                </div>
                <Timeline q={q} />
              </div>
            </>
          )}

          {isSigned && q.signatureDataUrl && (
            <>
              <div className="ed-divider" />
              <div>
                <div className="data-hd">
                  <span className="t-micro">Firma elettronica <Helper text="Firma elettronica semplice (SES) eIDAS. Legalmente valida per contratti commerciali nell'UE. Conserviamo immagine firma, IP firmatario, timestamp e hash SHA-256 del documento come prova." /></span>
                </div>
                <div className="p-3 mb-2 bg-paper-2 rounded-1 grid place-items-center" style={{ minHeight: 120 }}>
                  <img src={q.signatureDataUrl} alt="firma cliente" style={{ maxWidth: '100%', maxHeight: 100 }} />
                </div>
                <div className="ed-kv">
                  <InfoLine label="Firmato da" value={q.signerName ?? '—'} />
                  <InfoLine label="Email" value={q.signerEmail ?? '—'} />
                  <InfoLine label="Quando" value={fmtDate(q.signedAt)} mono />
                  <InfoLine label="IP" value={q.signerIp ?? '—'} mono />
                  <InfoLine label="Hash SHA-256" value={
                    <span className="mono text-[12px] mute break-all" title={q.documentHash ?? ''}>
                      {q.documentHash?.slice(0, 20)}…{q.documentHash?.slice(-8)}
                    </span>
                  } />
                </div>
              </div>
            </>
          )}

          {isRejected && (
            <>
              <div className="ed-divider" />
              <div>
                <div className="data-hd">
                  <span className="t-micro">Esito</span>
                </div>
                <div className="ed-kv">
                  <InfoLine label="Stato" value={<Chip tone="alert">{q.status === 'SCADUTO' ? 'Scaduto' : 'Rifiutato'}</Chip>} />
                  {q.rejectedAt && <InfoLine label="Quando" value={fmtDate(q.rejectedAt)} mono />}
                  {q.rejectionReason && <InfoLine label="Motivo" value={q.rejectionReason} />}
                </div>
              </div>
            </>
          )}

          {!isSent && signLinkCard && (
            <>
              <div className="ed-divider" />
              {signLinkCard}
            </>
          )}
        </div>
      </div>

      {sendModalOpen && (
        <SendQuoteModal
          quote={q}
          onClose={() => setSendModalOpen(false)}
          onSend={(p) => sendQuote.mutate(p)}
          sending={sendQuote.isPending}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
function Timeline({ q }: { q: QuoteFull }) {
  const events: Array<{ icon: IconName; label: string; at: string | null; tone?: 'ok' | 'info' | 'mute' }> = [
    { icon: 'send',  label: 'Inviato al cliente', at: q.sentAt,    tone: 'info' },
    { icon: 'eye',   label: 'Cliente ha aperto',  at: q.viewedAt,  tone: 'info' },
    { icon: 'check', label: 'Firmato',            at: q.signedAt,  tone: 'ok' },
    { icon: 'invoice', label: 'Convertito in fattura', at: q.convertedAt, tone: 'ok' },
  ].filter((e) => !!e.at) as any;

  if (!events.length) return <div className="mute text-[13px] italic">Nessun evento ancora.</div>;

  return (
    <div className="space-y-2.5">
      {events.map((e, i) => (
        <div key={i} className="flex items-start gap-2.5 text-[13px]">
          <div className="icon-tile sm shrink-0" style={{ ['--c' as any]: 'var(--accent)' }}>
            <Icon name={e.icon} size={12} />
          </div>
          <div className="flex-1">
            <div className="font-semibold">{e.label}</div>
            <div className="mute text-[12px] mono">{fmtDate(e.at!)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
function SendQuoteModal({ quote, onClose, onSend, sending }: {
  quote: QuoteFull;
  onClose: () => void;
  onSend: (p: { to: string; cc?: string; subject: string; message: string }) => void;
  sending: boolean;
}) {
  // Pre-fetch contatti cliente per autofill
  const { data: contacts } = useQuery({
    queryKey: ['client-contacts', quote.client.id],
    queryFn: () => api.get<ClientContact[]>(`/clients/${quote.client.id}/contacts`).catch(() => []),
  });
  const primary = contacts?.find((c) => c.isPrimary) ?? contacts?.[0];

  const [to, setTo] = useState(primary?.email ?? '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(quote.title ?? `Preventivo ${quote.number ?? ''}`.trim());
  const [message, setMessage] = useState(`Ciao,\n\ncome promesso ti mando il preventivo. Trovi tutto nel link qui sotto: puoi visualizzarlo e firmarlo direttamente online, in pochi secondi.\n\nFammi sapere se hai dubbi o serve qualche aggiustamento.\n\nUn saluto`);

  // Quando contacts arrivano dopo il render, autofill
  if (!to && primary?.email) setTo(primary.email);

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 quote-email-overlay" onClick={onClose}>
      <div className="bg-white rounded-[var(--r-card)] border border-line shadow-lg w-[520px] max-w-[92vw] quote-email-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Manda il preventivo al cliente">
        <div className="p-4 border-b border-line flex items-center justify-between quote-email-hd">
          <div>
            <div className="t-micro">Invio preventivo · firma elettronica eIDAS</div>
            <div className="t-h3 mt-0.5">Manda al cliente</div>
          </div>
          <button className="tb-icon-btn" onClick={onClose}><Icon name="x" /></button>
        </div>
        <div className="p-4 space-y-3 text-[13px] quote-email-bd">
          <div>
            <label className="field-label">
              Destinatario
              <Helper text="L'email a cui mandiamo il link firma. Default: contatto primario del cliente. Cambialo se devi mandarlo a un'altra persona." />
            </label>
            <input className="inp" placeholder="cliente@example.com" value={to} onChange={(e) => setTo(e.target.value)} />
            {contacts && contacts.length > 1 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {contacts.filter((c) => c.email).map((c) => (
                  <button key={c.id} className="btn btn-sm btn-dashed" onClick={() => setTo(c.email!)}>
                    {c.fullName}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="field-label">CC (opz)</label>
            <input className="inp" placeholder="ufficio@example.com" value={cc} onChange={(e) => setCc(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Oggetto</label>
            <input className="inp" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <label className="field-label">
              Messaggio
              <Helper text="Sarà la prima cosa che il cliente legge nell'email. Tonalità informale = più conversioni. Il link firma viene aggiunto automaticamente sotto." />
            </label>
            <textarea className="inp" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>
        <div className="p-4 border-t border-line flex gap-2 quote-email-ft">
          <button className="btn" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button
            className="btn btn-primary"
            disabled={!to.trim() || !subject.trim() || sending}
            onClick={() => onSend({ to: to.trim(), cc: cc.trim() || undefined, subject: subject.trim(), message: message.trim() })}
          >
            <Icon name="send" size={12} /> {sending ? 'Invio…' : 'Invia preventivo'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
/* Riga chiave-valore NUDA (.ed-kv-row): label maiuscola .k, valore .v, hairline tra le righe.
   Va sempre usata dentro un wrapper .ed-kv (l'ultima riga perde l'hairline). */
function InfoLine({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="ed-kv-row">
      <div className="k">{label}</div>
      <div className={`v ${mono ? 'mono' : ''}`}>{value}</div>
    </div>
  );
}
